/**
 * Quem escuta a sessão de cinema chegando, para o app inteiro.
 *
 * Porte da metade de vigia do `lib/features/watch_party/data/services/voice_chat_service.dart`
 * e de `.../incoming_call_validator.dart`. Olha a mesma ficha
 * `voice_rooms/{idDaSala}` que a sinalização da chamada usa, só que do lado de
 * quem é chamado: quando ela aparece com `state: 'calling'` e o chamado é a
 * gente, sai um convite e a tarja silenciosa aparece. Some quando a pessoa
 * entra na tela da sessão, quando a sala acaba, ou quando a ficha some.
 *
 * Mora no compartilhado porque isto aqui não é WebRTC: é o armazém e duas
 * fichas. Quem monta a ligação de verdade — o `RTCPeerConnection` do navegador
 * ou o pacote nativo do celular — fica em cada app, porque é ali que as duas
 * plataformas de fato divergem. O que decide se há convite não diverge.
 */

import { ligacao } from '../nucleo/ligacao';
import { lerData } from '../nucleo/tempo';
import { COLECAO_SALAS, type FichaDaSala, idDaSala } from './sala';

export const COLECAO_VOZ = 'voice_rooms';
export const COLECAO_CANDIDATOS = 'voice_candidates';

/** A ficha crua da chamada, do jeito que o Flutter grava. */
export interface FichaDaVoz {
  callerUid?: string;
  calleeUid?: string;
  callerName?: string;
  state?: string;
  offer?: string;
  answer?: string;
  mediaTitle?: string;
  mediaPosterPath?: string;
  mediaType?: string;
  tmdbId?: number;
  malId?: number;
  isAnime?: boolean;
  season?: number;
  episode?: number;
  updatedAt?: string;
}

export interface FichaDeCandidato {
  roomId?: string;
  from?: string;
  candidate?: string;
  sdpMid?: string | null;
  sdpMLineIndex?: number | null;
  createdAt?: string;
}

export interface ConviteDeSessao {
  sala: string;
  quemChama: string;
  nomeDeQuemChama: string;
  titulo: string;
  cartaz: string;
  tipo: string;
  tmdbId: number | null;
  malId: number | null;
  anime: boolean;
  temporada: number | null;
  episodio: number | null;
}

/** Quanto tempo o pulso da sala vale como "está acontecendo agora". */
export const JANELA_DO_CONVITE = 3 * 60_000;

/**
 * Se a ficha da chamada é um convite de verdade para este aparelho, agora.
 *
 * Porte de `incoming_call_validator.dart`, e função pura pelo mesmo motivo: é a
 * única parte disto que dá para conferir sem uma chamada de verdade no ar.
 */
export function convitePassa(op: {
  eu: string;
  voz: FichaDaVoz | null;
  salaAtiva: boolean;
  salaMexidaEm: Date | null;
  agora: Date;
  janela?: number;
}): boolean {
  const { eu, voz, salaAtiva, salaMexidaEm, agora, janela = JANELA_DO_CONVITE } = op;
  if (!voz) return false;
  const quemChama = voz.callerUid;
  const quemAtende = voz.calleeUid;
  if (!quemChama || quemChama === eu || quemAtende !== eu) return false;
  if (voz.state === 'ended' || voz.state === 'connected') return false;
  if (!salaAtiva || !salaMexidaEm) return false;
  return Math.abs(agora.getTime() - salaMexidaEm.getTime()) <= janela;
}

let convite: ConviteDeSessao | null = null;
const atentos = new Set<(c: ConviteDeSessao | null) => void>();
let soltarVoz: (() => void) | null = null;
let soltarSala: (() => void) | null = null;

/** O convite de agora, para quem nasce depois de ele chegar. */
export const conviteDeAgora = (): ConviteDeSessao | null => convite;

export function inscreverConvite(ouvinte: (c: ConviteDeSessao | null) => void): () => void {
  atentos.add(ouvinte);
  return () => atentos.delete(ouvinte);
}

function anunciar(novo: ConviteDeSessao | null): void {
  convite = novo;
  for (const o of atentos) o(novo);
}

function limparConvite(): void {
  if (convite === null) return;
  anunciar(null);
}

/**
 * Passa a escutar sessões de cinema chegando.
 *
 * Chamar de novo só troca o ouvinte por um preso aos ids novos. Sem a outra
 * pessoa não há o que escutar.
 */
export function escutarConvites(eu: string, parceiro: string | null): void {
  pararDeEscutarConvites();
  if (!eu || !parceiro) return;

  const { armazem } = ligacao();
  const sala = idDaSala(eu, parceiro);

  const avaliar = () => {
    const voz = armazem.documento<FichaDaVoz>(COLECAO_VOZ, sala);
    /* A sala de voz só quer dizer alguma coisa enquanto a sala de cinema
       correspondente existe. Uma deixada em `calling` por uma aba fechada ou
       por uma sessão velha não pode voltar como convite. */
    const cinema = armazem.documento<FichaDaSala>(COLECAO_SALAS, sala);
    if (
      !convitePassa({
        eu,
        voz,
        salaAtiva: cinema?.active === true,
        salaMexidaEm: lerData(cinema?.updatedAt),
        agora: new Date(),
      })
    ) {
      limparConvite();
      return;
    }

    const novo: ConviteDeSessao = {
      sala,
      quemChama: voz?.callerUid ?? '',
      nomeDeQuemChama: voz?.callerName ?? 'Partner',
      titulo: voz?.mediaTitle ?? '',
      cartaz: voz?.mediaPosterPath ?? '',
      tipo: voz?.mediaType ?? 'movie',
      tmdbId: typeof voz?.tmdbId === 'number' ? voz.tmdbId : null,
      malId: typeof voz?.malId === 'number' ? voz.malId : null,
      anime: voz?.isAnime === true,
      temporada: typeof voz?.season === 'number' ? voz.season : null,
      episodio: typeof voz?.episode === 'number' ? voz.episode : null,
    };

    /* O pulso da sala bate de poucos em poucos segundos: a tarja só é mexida
       quando o convite em si muda. */
    if (
      convite &&
      convite.sala === novo.sala &&
      convite.quemChama === novo.quemChama &&
      convite.titulo === novo.titulo
    ) {
      return;
    }
    anunciar(novo);
  };

  soltarVoz = armazem.inscrever(COLECAO_VOZ, avaliar);
  soltarSala = armazem.inscrever(COLECAO_SALAS, avaliar);
  avaliar();
}

/**
 * Para de escutar. Chamado ao entrar na tela da sessão, para o aviso não brigar
 * com a chamada que está acontecendo, e ao sair da conta.
 */
export function pararDeEscutarConvites(): void {
  soltarVoz?.();
  soltarSala?.();
  soltarVoz = null;
  soltarSala = null;
  limparConvite();
}

/** " · S2E4", e só quando é série e os dois números existem. */
export function sufixoDoEpisodio(c: ConviteDeSessao): string {
  if (c.tipo !== 'tv') return '';
  if (c.temporada === null || c.episodio === null) return '';
  return ` · S${c.temporada}E${c.episodio}`;
}
